import React, {
  ComponentType,
  createContext,
  MutableRefObject,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { Router, Route, Switch, useLocation, useSearch } from 'wouter'
import type { BasedClient } from '@based/sdk/client'
import {
  fire,
  idFrom,
  join,
  normalizeDot,
  paramsBag,
  sanitizeParams,
  slash,
  stripParamsPath,
} from './utils.js'
import { NewCMSTab, NewCMSTabProps, TabFallback, TabLayout } from './Tab.js'
import { NewCMSPage, NewCMSPageProps, PageLayout } from './Page.js'
import { RootLayout, RootRedirect } from './Root.js'
import { AuthLayout, NewCMSAuthorizeProps, NewCMSAuthorize } from './Auth.js'
import { IconProps } from '../Icon/index.js'
import { capitalize, normalize } from '../../utils/text.js'

export type NewCMSNavigateOptions = { replace?: boolean; state?: any }

export type NewCMSParams = {
  get: (key: string) => string | undefined
  has: (key: string) => boolean
} & Readonly<Record<string, string | undefined>>

export type NewCMSRouteInfo = {
  id: string
  title: string
  path: string
  group?: string
}

export type NewCMSEventContext = {
  client: BasedClient
  base: string
  location: string
  search: string
  query: URLSearchParams
  params: NewCMSParams
  navigate: (to: string, options?: NewCMSNavigateOptions) => void
  getID: () => string | undefined
  from: string
  to: string
  route: NewCMSRouteInfo
}

export type NewCMSRouteEvents = {
  beforeEnter?: (ctx: NewCMSEventContext) => void
  enter?: (ctx: NewCMSEventContext) => void
  beforeLeave?: (ctx: NewCMSEventContext) => void
  leave?: (ctx: NewCMSEventContext) => void
}

export type NewCMSAuthorizeFn = (
  ctx: Omit<NewCMSEventContext, 'route'>,
) => boolean | Promise<boolean>

type NewCMSBaseValue = {
  client: BasedClient
  base: string
  location: string
  search: string
  query: URLSearchParams
  navigate: (to: string, options?: NewCMSNavigateOptions) => void
}

type NewCMSRuntimeValue = {
  id?: string
  params: Record<string, string>
}

export type NewCMSValue = NewCMSBaseValue & {
  params: NewCMSParams
  getID: () => string | undefined
}

const NewCMSBaseContext = createContext<NewCMSBaseValue | null>(null)
const NewCMSRuntimeContext = createContext<NewCMSRuntimeValue>({
  id: undefined,
  params: {},
})

const NewCMSNavContext = createContext<{
  fromRef: MutableRefObject<string>
  toRef: MutableRefObject<string>
} | null>(null)

export function useNewCMS(): NewCMSValue {
  const base = useContext(NewCMSBaseContext)
  if (!base) throw new Error('useNewCMS must be used inside <NewCMS />')

  const runtime = useContext(NewCMSRuntimeContext)

  return {
    ...base,
    params: paramsBag(runtime.params),
    getID: () => runtime.id,
  }
}

export type NewCMSGroupProps = {
  title?: string
  children?: ReactNode
}

export type NewCMSNotFoundProps = {
  component?: ComponentType<any>
  children?: ReactNode
  on?: NewCMSRouteEvents
}

function NewCMSGroup(_: NewCMSGroupProps) {
  return null
}

function NewCMSNotFound(_: NewCMSNotFoundProps) {
  return null
}

export type ParsedPage = {
  id: string
  title: string
  icon?: IconProps['variant']
  counter?: number
  rel: string
  abs: string
  nav: string
  group?: string
  component?: React.ComponentType<any>
  children?: ReactNode
  authorize?: NewCMSAuthorizeFn
  on?: NewCMSRouteEvents
}

export type ParsedTab = {
  id: string
  title: string
  icon?: IconProps['variant']
  abs: string
  pages: ParsedPage[]
}

type Parsed = {
  tabs: ParsedTab[]
  Authorize?: {
    component?: React.ComponentType<any>
    children?: ReactNode
    on?: NewCMSRouteEvents
  }
  NotFound?: {
    component?: React.ComponentType<any>
    children?: ReactNode
    on?: NewCMSRouteEvents
  }
  dotMap: Map<string, string>
}

function parse(children: ReactNode): Parsed {
  const tabs: ParsedTab[] = []
  let Authorize: Parsed['Authorize']
  let NotFound: Parsed['NotFound']

  const rootPages: ReactElement[] = []

  React.Children.forEach(children, (ch) => {
    if (!React.isValidElement(ch)) return

    if (ch.type === NewCMSAuthorize) {
      const p = ch.props as NewCMSAuthorizeProps
      Authorize = { component: p.component, children: p.children, on: p.on }
      return
    }

    if (ch.type === NewCMSNotFound) {
      const p = ch.props as NewCMSNotFoundProps
      NotFound = { component: p.component, children: p.children, on: p.on }
      return
    }

    if (ch.type === NewCMSTab) {
      const props = ch.props as NewCMSTabProps
      const tabId = idFrom(undefined, props.title)
      const tabAbs = slash(props.path ?? normalize(props.title))

      const pages: ParsedPage[] = []

      const pushPage = (c: React.ReactElement, group?: string) => {
        const pageProps = c.props as NewCMSPageProps
        const pageId = `${tabId}.${idFrom(undefined, pageProps.title)}`
        const rel = slash(pageProps.path ?? normalize(pageProps.title))
        const abs = join(tabAbs, rel)
        const nav = stripParamsPath(abs)

        pages.push({
          id: pageId,
          title: capitalize(pageProps.title),
          rel,
          abs,
          nav,
          group,
          ...pageProps,
        })
      }

      React.Children.forEach(props.children, (c) => {
        if (!React.isValidElement(c)) return

        if (c.type === NewCMSPage) {
          pushPage(c)
          return
        }

        if (c.type === NewCMSGroup) {
          const gp = c.props as NewCMSGroupProps
          React.Children.forEach(gp.children, (cc) => {
            if (!React.isValidElement(cc) || cc.type !== NewCMSPage) return
            pushPage(cc, gp.title)
          })
        }
      })

      tabs.push({
        id: tabId,
        title: props.title,
        icon: props.icon as IconProps['variant'],
        abs: tabAbs,
        pages,
      })
      return
    }

    if (ch.type === NewCMSPage) rootPages.push(ch)
  })

  if (tabs.length === 0) {
    const tabId = 'default'
    const tabAbs = '/'
    const pages: ParsedPage[] = []

    for (const ch of rootPages) {
      const pageProps = ch.props as NewCMSPageProps
      const pageId = `${tabId}.${idFrom(undefined, pageProps.title)}`
      const rel = slash(pageProps.path ?? normalize(pageProps.title))
      const abs = join(tabAbs, rel)
      const nav = stripParamsPath(abs)

      pages.push({
        id: pageId,
        title: capitalize(pageProps.title),
        rel,
        abs,
        nav,
        ...pageProps,
      })
    }

    tabs.push({ id: tabId, title: 'default', abs: '/', pages })
  }

  const dotMap = new Map<string, string>()

  for (const t of tabs) {
    dotMap.set(normalizeDot(t.title), t.abs)
    for (const p of t.pages) {
      dotMap.set(normalizeDot(`${t.title}.${p.title}`), p.nav)
    }
  }

  return { tabs, Authorize, NotFound, dotMap }
}

function Guard({
  page,
  mergedParams,
}: {
  page: ParsedPage
  mergedParams: Record<string, string>
}) {
  const cms = useNewCMS()
  const nav = useContext(NewCMSNavContext)

  const from = nav?.fromRef.current ?? cms.location
  const to = nav?.toRef.current ?? cms.location

  const params = paramsBag(mergedParams)

  const route: NewCMSRouteInfo = {
    id: page.id,
    title: page.title,
    path: page.nav,
    group: page.group,
  }

  useEffect(() => {
    const ctx: NewCMSEventContext = { ...cms, from, to, route, params }

    fire(page.on, 'beforeEnter', ctx)

    let alive = true

    const run = async () => {
      if (page.authorize) {
        const allowed = await page.authorize({ ...cms, from, to, params })
        if (!alive) return
        if (!allowed) {
          cms.navigate('/auth', { replace: true })
          return
        }
      }

      fire(page.on, 'enter', ctx)
    }

    run()

    return () => {
      alive = false
      fire(page.on, 'beforeLeave', ctx)
      fire(page.on, 'leave', ctx)
    }
  }, [page.id, cms.location])

  const content = page.component ? <page.component /> : (page.children ?? null)

  return (
    <NewCMSRuntimeContext.Provider
      value={{ id: page.id, params: mergedParams }}
    >
      <PageLayout page={page}>{content}</PageLayout>
    </NewCMSRuntimeContext.Provider>
  )
}

function SpecialRoute({
  id,
  title,
  path,
  on,
  component: Comp,
  children,
}: {
  id: string
  title: string
  path: string
  on?: NewCMSRouteEvents
  component?: React.ComponentType<any>
  children?: ReactNode
}) {
  const cms = useNewCMS()
  const nav = useContext(NewCMSNavContext)

  const from = nav?.fromRef.current ?? cms.location
  const to = nav?.toRef.current ?? cms.location

  const route: NewCMSRouteInfo = { id, title, path }

  useEffect(() => {
    const ctx: NewCMSEventContext = {
      ...cms,
      from,
      to,
      route,
      params: cms.params,
    }

    fire(on, 'beforeEnter', ctx)
    fire(on, 'enter', ctx)

    return () => {
      fire(on, 'beforeLeave', ctx)
      fire(on, 'leave', ctx)
    }
  }, [id, cms.location])

  return (
    <NewCMSRuntimeContext.Provider value={{ id, params: {} }}>
      {Comp ? <Comp /> : (children ?? null)}
    </NewCMSRuntimeContext.Provider>
  )
}

export type NewCMSProps = {
  client: BasedClient
  title: string
  base?: string
  children?: ReactNode
  leadComponent?: ReactNode
  trailComponent?: ReactNode
}

function NewCMSRoot({
  client,
  title,
  base = '',
  children,
  leadComponent,
  trailComponent,
}: NewCMSProps) {
  return (
    <Router base={base}>
      <NewCMSInner
        client={client}
        title={title}
        base={base}
        leadComponent={leadComponent}
        trailComponent={trailComponent}
      >
        {children}
      </NewCMSInner>
    </Router>
  )
}

function NewCMSInner({
  client,
  title = '',
  base,
  children,
  leadComponent,
  trailComponent,
}: NewCMSProps) {
  window.document.title = title

  const parsed = useMemo(() => parse(children), [children])
  const [location, setLoc] = useLocation()
  const search = useSearch()
  const query = useMemo(() => new URLSearchParams(search), [search])

  const fromRef = useRef(location)
  const toRef = useRef(location)

  const navigate = (target: string, options?: NewCMSNavigateOptions) => {
    const isPath = target.startsWith('/')
    const resolved = !isPath
      ? parsed.dotMap.get(normalizeDot(target))
      : undefined
    const next = slash(resolved ?? target)

    fromRef.current = location
    toRef.current = next

    setLoc(next, options as any)
  }

  useEffect(() => {
    toRef.current = location
  }, [location])

  const baseValue: NewCMSBaseValue = {
    client,
    base,
    location,
    search,
    query,
    navigate,
  }

  const firstAbs = parsed.tabs[0]?.pages[0]?.nav

  return (
    <NewCMSBaseContext.Provider value={baseValue}>
      <NewCMSNavContext.Provider value={{ fromRef, toRef }}>
        <Switch>
          {parsed.Authorize ? (
            <Route path="/auth">
              <AuthLayout>
                <SpecialRoute
                  id="auth"
                  title="Authorize"
                  path="/auth"
                  on={parsed.Authorize.on}
                  component={parsed.Authorize.component}
                >
                  {parsed.Authorize.children}
                </SpecialRoute>
              </AuthLayout>
            </Route>
          ) : null}

          <Route>
            <RootLayout
              tabs={parsed.tabs}
              leadComponent={leadComponent}
              trailComponent={trailComponent}
            >
              <Switch>
                {parsed.NotFound ? (
                  <Route path="/404">
                    <SpecialRoute
                      id="404"
                      title="NotFound"
                      path="/404"
                      on={parsed.NotFound.on}
                      component={parsed.NotFound.component}
                    >
                      {parsed.NotFound.children}
                    </SpecialRoute>
                  </Route>
                ) : null}

                <Route path="/">
                  <RootRedirect to={firstAbs ? firstAbs : '/404'} />
                </Route>

                {parsed.tabs.map((tab) => (
                  <Route key={tab.id} path={tab.abs} nest>
                    {(tabParams: any) => {
                      const tabP = sanitizeParams(tabParams)

                      return (
                        <TabLayout tab={tab}>
                          <Switch>
                            {tab.pages.map((page) => (
                              <Route key={page.id} path={page.rel}>
                                {(pageParams: any) => {
                                  const pageP = sanitizeParams(pageParams)
                                  const merged = { ...tabP, ...pageP }
                                  return (
                                    <Guard page={page} mergedParams={merged} />
                                  )
                                }}
                              </Route>
                            ))}

                            <Route>
                              <TabFallback tab={tab} />
                            </Route>
                          </Switch>
                        </TabLayout>
                      )
                    }}
                  </Route>
                ))}

                <Route>
                  <RootRedirect to="/404" />
                </Route>
              </Switch>
            </RootLayout>
          </Route>
        </Switch>
      </NewCMSNavContext.Provider>
    </NewCMSBaseContext.Provider>
  )
}

export const NewCMS = Object.assign(NewCMSRoot, {
  Tab: NewCMSTab,
  Page: NewCMSPage,
  Group: NewCMSGroup,
  Authorize: NewCMSAuthorize,
  NotFound: NewCMSNotFound,
})
